export interface ParsedPostalAddress {
  address: string;
  zipCode: string;
  city: string;
  fillAddressLater: boolean;
}

export interface ParsedBookingPayload extends ParsedPostalAddress {
  phone: string;
  name: string;
  model: string;
  guests: string;
  notes: string;
  rawAddress: string;
}

const EMPTY_ADDRESS: ParsedPostalAddress = {
  address: "",
  zipCode: "",
  city: "",
  fillAddressLater: false,
};

function normalizeLabel(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function parsePostalAddress(rawAddress?: string | null): ParsedPostalAddress {
  const value = (rawAddress || "").trim();
  if (!value) return EMPTY_ADDRESS;

  if (normalizeLabel(value).includes("remplir plus tard")) {
    return { ...EMPTY_ADDRESS, fillAddressLater: true };
  }

  const commaMatch = value.match(/^(.*),\s*(\d{5})\s+(.+)$/);
  const spaceMatch = value.match(/^(.*)\s+(\d{5})\s+(.+)$/);
  const match = commaMatch || spaceMatch;

  if (!match) {
    return {
      address: value,
      zipCode: "",
      city: "",
      fillAddressLater: false,
    };
  }

  return {
    address: match[1].replace(/,\s*$/, "").trim(),
    zipCode: match[2].trim(),
    city: match[3].trim(),
    fillAddressLater: false,
  };
}

export function parseBookingPayload(payload?: string | null): ParsedBookingPayload {
  const raw = (payload || "").trim();
  const details = {
    phone: "",
    name: "",
    model: "",
    guests: "",
    notes: "",
    rawAddress: "",
  };

  if (!raw) {
    return { ...details, ...EMPTY_ADDRESS };
  }

  const segments = raw.includes("|") ? raw.split("|") : [raw];

  for (const segment of segments) {
    const separatorIndex = segment.indexOf(":");
    if (separatorIndex === -1) continue;

    const label = normalizeLabel(segment.slice(0, separatorIndex));
    const value = segment.slice(separatorIndex + 1).trim();

    if (["tel", "telephone", "phone"].includes(label)) {
      details.phone = value;
    } else if (label === "nom" || label === "name") {
      details.name = value;
    } else if (label === "adresse" || label === "address") {
      details.rawAddress = value;
    } else if (label === "modele" || label === "model") {
      details.model = value;
    } else if (label === "invites" || label === "guests") {
      details.guests = value;
    } else if (label === "notes") {
      details.notes = value;
    }
  }

  if (!raw.includes("|") && !details.phone) {
    details.phone = raw;
  }

  return {
    ...details,
    ...parsePostalAddress(details.rawAddress),
  };
}
